import { AdjustChip, AdjustChipGroup, AdjustGroupHeader, AdjustNewBadge, AdjustNote } from '../AdjustControlPrimitives.component'
import { BLUE_100, BLUE_600, GRAY_300, GRAY_800, WHITE } from 'constants/styling/theme'
import { Box, Stack } from '@mui/material'
import { FC, useRef, useState } from 'react'
import { STICKER_ICONS, StickerFont } from '../adjust.types'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { AdjustSwatches } from '../AdjustSwatches.component'
import { LabSegmented } from '../../lab/LabPanelPrimitives.component'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUITooltip } from 'components/common/MUITooltip'
import { STICKER_ICON_COMPONENTS } from '../adjustStickers'
import { SelectedStickerControls } from './SelectedStickerControls.component'
import UploadRoundedIcon from '@mui/icons-material/UploadRounded'
import { useAdjustEditor } from '../AdjustEditor.context'
import { useAdjustStickerActions } from '../useAdjustStickerActions.hook'
import { useTranslation } from 'react-i18next'

const TEXT_PRESETS = ['sold', 'new_listing', 'open_house', 'price_reduced']

const FONT_OPTIONS: Array<{ value: StickerFont, label: string }> = [
  { value: 'sans', label: 'Inter' },
  { value: 'brand', label: 'Rhetorik' },
]

/** Real-estate icons, text labels in the brand fonts, and the agency's own logo — no emoji (brand guide) */
export const StickersControls: FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { doc, apply, stickerTab, setStickerTab, stickerColor, setStickerColor, stickerFont, setStickerFont, selectedStickerId } = useAdjustEditor()
  const { addIcon, addText, addLogo } = useAdjustStickerActions()
  const [text, setText] = useState(() => t('adjust.stickers.text_presets.sold'))
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selected = doc.stickers.find(sticker => sticker.id === selectedStickerId) ?? null

  /** A colour pick also recolours the selected icon or text */
  const pickColor = (color: string) => {
    setStickerColor(color)
    if (selected && selected.kind !== 'logo') apply({ type: 'updateSticker', id: selected.id, patch: { color } })
  }

  return (
    <Stack gap="1.2rem">

      <LabSegmented
        ariaLabel={t('manual_tools.STICKER')}
        disabled={disabled}
        value={stickerTab}
        onChange={setStickerTab}
        options={[
          { value: 'icons', label: t('adjust.stickers.tab.icons') },
          { value: 'text', label: t('adjust.stickers.tab.text') },
          { value: 'logo', label: t('adjust.stickers.tab.logo'), adornment: <AdjustNewBadge /> },
        ]}
      />

      {stickerTab === 'icons' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.6rem' }}>
          {STICKER_ICONS.map((icon) => {
            const Icon = STICKER_ICON_COMPONENTS[icon]
            return (
              <MUITooltip key={icon} content={t(`adjust.stickers.icon.${icon}`)}>
                <Box
                  component="button"
                  type="button"
                  aria-label={t(`adjust.stickers.icon.${icon}`)}
                  disabled={disabled}
                  onClick={() => addIcon(icon)}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '0.8rem',
                    border: `1px solid ${GRAY_300}`,
                    backgroundColor: WHITE,
                    color: GRAY_800,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    '&:hover': { borderColor: BLUE_600, backgroundColor: BLUE_100 },
                    '&:focus-visible': { outline: `2px solid ${BLUE_600}`, outlineOffset: 2 },
                    '& .MuiSvgIcon-root': { fontSize: '2.4rem' },
                  }}
                >
                  <Icon />
                </Box>
              </MUITooltip>
            )
          })}
        </Box>
      )}

      {stickerTab === 'text' && (
        <Stack gap="1rem">

          <MUIInputField label={t('adjust.stickers.text_label')} value={text} maxLength={60} disabled={disabled} onChange={(event) => setText(event.target.value)} />

          <AdjustChipGroup>
            {TEXT_PRESETS.map(preset => (
              <AdjustChip key={preset} label={t(`adjust.stickers.text_presets.${preset}`)} disabled={disabled} onClick={() => setText(t(`adjust.stickers.text_presets.${preset}`))} />
            ))}
          </AdjustChipGroup>

          <LabSegmented ariaLabel={t('adjust.stickers.font')} disabled={disabled} value={stickerFont} onChange={setStickerFont} options={FONT_OPTIONS} />

          <MUIButton type="secondaryBorder" size="sm" startIcon={<AddRoundedIcon />} disabled={disabled || !text.trim()} onClick={() => addText(text)}>
            {t('adjust.stickers.add_text')}
          </MUIButton>

        </Stack>
      )}

      {stickerTab === 'logo' && (
        <Stack gap="1rem">

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            hidden
            onChange={(event) => {
              const logo = event.target.files?.[0]
              if (logo) addLogo(logo)
              event.target.value = ''
            }}
          />

          <MUIButton type="secondaryBorder" size="sm" startIcon={<UploadRoundedIcon />} disabled={disabled} onClick={() => fileInputRef.current?.click()}>
            {t('adjust.stickers.upload_logo')}
          </MUIButton>

          <AdjustNote>{t('adjust.stickers.logo_hint')}</AdjustNote>

        </Stack>
      )}

      {stickerTab !== 'logo' && (
        <>

          <AdjustGroupHeader title={t('adjust.color')} />

          <AdjustSwatches value={stickerColor} onChange={pickColor} disabled={disabled} />

        </>
      )}

      <AdjustGroupHeader title={t('adjust.stickers.selected')} />

      {selected && <SelectedStickerControls sticker={selected} disabled={disabled} />}

      {!selected && <AdjustNote>{t('adjust.stickers.select_hint')}</AdjustNote>}

    </Stack>
  )
}
